import { DeleteOutlined } from '@ant-design/icons';
import { L } from '@lib';
import { Button, Input, InputNumber, Select, Switch, Typography } from 'antd';
import React from 'react';

const { Text } = Typography;

interface IProps {
  index: number;
  item: any;
  categoryOptions: { label: string; value: string }[];
  serviceOptions?: { id: number; name: string }[];
  onChange: (index: number, field: string, value: any) => void;
  onRemove: (index: number) => void;
}

const lbl: React.CSSProperties = {
  fontSize: 11,
  color: '#8c8c8c',
  display: 'block',
  marginBottom: 2,
  textTransform: 'uppercase',
  fontWeight: 600,
  letterSpacing: 0.3,
};

export const ParsedItemCard: React.FC<IProps> = ({ index, item, categoryOptions, serviceOptions, onChange, onRemove }) => {
  const qty = item.quantity || 0;
  const sell = item.sellPrice || 0;
  const cost = item.costPrice || 0;
  const margin = sell > 0 ? ((sell - cost) / sell) * 100 : 0;
  const total = qty * sell;
  const hasServiceOptions = !!(serviceOptions && serviceOptions.length > 0);
  const serviceSelectOptions = (serviceOptions || []).map((s) => ({ label: s.name, value: s.id }));

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #f0f7ff 0%, #f8fbff 100%)',
        borderRadius: 10,
        border: '1px solid #d4e4f7',
        padding: '14px 14px 12px',
        marginBottom: 10,
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          borderRadius: 6,
          background: '#2CBDFF',
          color: '#fff',
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {index + 1}
      </div>
      <div style={{ paddingLeft: 32 }}>
        {hasServiceOptions && (
          <div style={{ marginBottom: 10 }}>
            <Text style={lbl}>{L('Assign to service')}</Text>
            <Select
              size="small"
              allowClear
              placeholder={L('Inventory only (no service)')}
              style={{ width: '100%' }}
              value={item.serviceId}
              options={serviceSelectOptions}
              onChange={(v) => onChange(index, 'serviceId', v)}
            />
          </div>
        )}

        {/* Row 1: Name, Reference, Part #, Delete */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 2 }}>
            <Text style={lbl}>{L('Name')}</Text>
            <Input size="small" value={item.name} onChange={(e) => onChange(index, 'name', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text style={lbl}>{L('Reference')}</Text>
            <Input
              size="small"
              placeholder={L('Stock code')}
              value={item.reference || ''}
              onChange={(e) => onChange(index, 'reference', e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text style={lbl}>{L('Part #')}</Text>
            <Input
              size="small"
              placeholder={L('From invoice')}
              value={item.partNumber || ''}
              onChange={(e) => onChange(index, 'partNumber', e.target.value)}
            />
          </div>
          <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => onRemove(index)} />
        </div>

        {/* Row 2: Category + Description */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <Text style={lbl}>{L('Category')}</Text>
            <Select
              size="small"
              allowClear
              placeholder={L('Select category')}
              style={{ width: '100%' }}
              value={item.categoryId}
              options={categoryOptions}
              onChange={(v) => onChange(index, 'categoryId', v)}
            />
          </div>
          <div style={{ flex: 2 }}>
            <Text style={lbl}>{L('Description')}</Text>
            <Input
              size="small"
              placeholder={L('Optional description')}
              value={item.description || ''}
              onChange={(e) => onChange(index, 'description', e.target.value)}
            />
          </div>
        </div>

        {/* Row 2b: Regular item + location (location always visible) */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, paddingBottom: 4 }}>
            <Switch
              size="small"
              checked={!!item.isInShop}
              onChange={(v) => onChange(index, 'isInShop', v)}
            />
            <Text style={{ fontSize: 12 }}>{L('Regular item')}</Text>
          </div>
          <div style={{ flex: 1 }}>
            <Text style={lbl}>{L('Location')}</Text>
            <Input
              size="small"
              placeholder={L('e.g. A3-12')}
              value={item.location || ''}
              onChange={(e) => onChange(index, 'location', e.target.value)}
            />
          </div>
        </div>

        {/* Row 3: Qty, Cost, Sale, Margin, Total */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <Text style={lbl}>{L('Qty')}</Text>
            <InputNumber
              size="small"
              value={item.quantity}
              onChange={(v) => onChange(index, 'quantity', v)}
              min={0}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text style={lbl}>{L('Cost (£)')}</Text>
            <InputNumber
              size="small"
              value={item.costPrice}
              onChange={(v) => onChange(index, 'costPrice', v)}
              min={0}
              step={0.01}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text style={lbl}>{L('Sale (£)')}</Text>
            <InputNumber
              size="small"
              value={item.sellPrice}
              onChange={(v) => onChange(index, 'sellPrice', v)}
              min={0}
              step={0.01}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1, textAlign: 'right', paddingBottom: 6 }}>
            <Text style={lbl}>{L('Margin')}</Text>
            <span style={{ fontSize: 13, color: margin >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>
              {margin.toFixed(1)}%
            </span>
          </div>
          <div style={{ flex: 1, textAlign: 'right', paddingBottom: 6 }}>
            <Text style={lbl}>{L('Total')}</Text>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>£{total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
